import React, { useContext, useEffect } from "react";
import PagePreview from "./Preview";
import { useSelector } from "react-redux";
import { FullGridContext } from "../..";
import { getData } from "~/functions/gridFunctions";
import PageContainer from "~/components/PageContainer";
import { PageContext } from "~/App";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { accountLabel, monthLabel } from "./monthOptions";

export function FinancialConciliation() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { pages } = useContext(PageContext);
  const paths = pathname.split("/");
  const redirectRouteName = pages
    .find((page) => page.name === paths[1])
    .children.find((page) => page.path === `/${paths[1]}/${paths[2]}`)
    .children[0].path;
  useEffect(() => {
    if (pathname.toUpperCase() === `/${paths[1]}/${paths[2]}`.toUpperCase()) {
      navigate(`${redirectRouteName}`);
    }
  }, [pathname]);
  return <Outlet />;
}

function safeDate(value) {
  if (!value) return "-";
  try {
    return format(parseISO(value), "MM/dd/yyyy");
  } catch (err) {
    return "-";
  }
}

export default function BankConciliation() {
  const filial = useSelector((state) => state.auth.filial);
  const defaultOrderBy = { column: "year", asc: false };
  const defaultGridHeader = [
    {
      title: "Account",
      name: "account",
      type: "text",
      filter: false,
    },
    {
      title: "Month",
      name: "month",
      type: "text",
      filter: false,
    },
    {
      title: "Covered Until",
      name: "last_imported_date",
      type: "text",
      filter: false,
    },
    {
      title: "Movements",
      name: "total_items",
      type: "text",
      filter: false,
    },
    {
      title: "Review",
      name: "review_items",
      type: "text",
      filter: false,
    },
    {
      title: "No Match",
      name: "pending_items",
      type: "text",
      filter: false,
    },
    {
      title: "Status",
      name: "status",
      type: "text",
      filter: true,
    },
  ];

  const {
    accessModule,
    limit,
    opened,
    orderBy,
    page,
    search,
    setGridData,
    setGridDetails,
    setLoadingData,
    setPage,
    setPages,
    setSearch,
  } = useContext(FullGridContext);

  const pageAccess = accessModule.children.find(
    (el) => el.alias === "bank-conciliations"
  );

  useEffect(() => {
    setSearch("");
    setPage(1);
  }, []);

  async function loader() {
    setLoadingData(true);
    try {
      const data = await getData("bankconciliation-months", {
        limit,
        page,
        orderBy,
        setPages,
        setGridData,
        search,
        defaultGridHeader,
        defaultOrderBy,
        setGridDetails,
      });
      if (!data) {
        setGridData([]);
        setLoadingData(false);
        return;
      }
      const gridDataValues = data.map((row, index) => {
        const {
          id,
          bankAccount,
          year,
          month,
          last_imported_date,
          total_items,
          pending_items,
          review_items,
          status,
          canceled_at,
        } = row;

        return {
          show: true,
          id,
          fields: [
            accountLabel(bankAccount),
            monthLabel(month, year),
            safeDate(last_imported_date),
            String(total_items ?? 0),
            String(review_items ?? 0),
            String(pending_items ?? 0),
            status,
          ],
          canceled: canceled_at,
          page: Math.ceil((index + 1) / limit),
        };
      });
      setGridData(gridDataValues);
      setLoadingData(false);
    } catch (err) {
      toast("Could not load bank reconciliations.", {
        type: "error",
        autoClose: 3000,
      });
      setGridData([]);
      setLoadingData(false);
    }
  }

  useEffect(() => {
    if (!opened) {
      loader();
    }
  }, [opened, filial, orderBy, search, limit, page]);

  return (
    <PageContainer
      FullGridContext={FullGridContext}
      PagePreview={PagePreview}
      pageAccess={pageAccess}
      defaultGridHeader={defaultGridHeader}
    />
  );
}
